import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { shouldDenyWorkspacePermissionAccess } from '../lib/workspacePermissionGate';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  useWorkflowPresets,
  useCreateWorkflowPreset,
  useUpdateWorkflowPreset,
  useDeleteWorkflowPreset,
} from '../hooks/useWorkflowPresets';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { WorkflowPresetDto } from '@prometeus/contracts';
import type { TagDto } from '@prometeus/contracts';
import { FavoriteStarIcon } from '../components/icons/FavoriteStarIcon';
import { uploadWorkflowPresetThumbnail } from '../lib/workflow-presets.api';
import { useQueryClient } from '@tanstack/react-query';

const FAVORITES_STORAGE_KEY = 'workflow-preset-favorites';
/** z-index dos modais: acima da sidebar (z-40) e do header (z-40) para o overlay cobrir a tela inteira */
const MODAL_OVERLAY_Z = 9998;

function getStoredFavorites(workspaceId: string): string[] {
  try {
    const raw = localStorage.getItem(`${FAVORITES_STORAGE_KEY}-${workspaceId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function setStoredFavorites(workspaceId: string, ids: string[]) {
  try {
    localStorage.setItem(`${FAVORITES_STORAGE_KEY}-${workspaceId}`, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

type MemberSummary = {
  userId: string;
  name: string;
  avatarUrl: string | null;
};

export const Route = createFileRoute('/w/$workspaceId/workflow-presets/')({
  component: WorkflowPresetsListPage,
});

function WorkflowPresetsListPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);

  const canAccess = hasPermission('workflow_presets.read');

  useEffect(() => {
    if (
      !shouldDenyWorkspacePermissionAccess({
        pathname: location.pathname,
        isLoadingPermissions,
        hasAccess: canAccess,
      })
    ) {
      return;
    }
    showPermissionDenied('Ver Workflow Presets');
    navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
  }, [
    location.pathname,
    isLoadingPermissions,
    canAccess,
    navigate,
    showPermissionDenied,
    workspaceId,
  ]);

  const { data: presets, isLoading } = useWorkflowPresets({
    enabled: canAccess,
  });
  const createPreset = useCreateWorkflowPreset();
  const updatePreset = useUpdateWorkflowPreset();

  const [favoriteIds, setFavoriteIds] = useState<string[]>(() =>
    getStoredFavorites(workspaceId),
  );
  useEffect(() => {
    setStoredFavorites(workspaceId, favoriteIds);
  }, [workspaceId, favoriteIds]);

  const toggleFavorite = useCallback(
    (e: React.MouseEvent, presetId: string) => {
      e.stopPropagation();
      setFavoriteIds((prev) =>
        prev.includes(presetId)
          ? prev.filter((id) => id !== presetId)
          : [...prev, presetId],
      );
    },
    [],
  );

  const { data: membersRaw } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const response = await api.get('/members');
      return response.data.data;
    },
    enabled: !!workspaceId && canAccess,
  });

  const { data: allTags = [] } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const response = await api.get('/tags');
      return response.data.data || [];
    },
    enabled: !!workspaceId && canAccess,
  });

  const memberByUserId: Record<string, MemberSummary> = useMemo(() => {
    const map: Record<string, MemberSummary> = {};
    if (!membersRaw) return map;
    for (const m of membersRaw as any[]) {
      const userId = m.user?.id;
      if (!userId) continue;
      const name = m.user?.name || m.user?.email || 'Membro sem nome';
      const avatarUrl = m.user?.avatarUrl ?? null;
      map[userId] = { userId, name, avatarUrl };
    }
    return map;
  }, [membersRaw]);

  const [searchInput, setSearchInput] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [tagsModalPresetId, setTagsModalPresetId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [thumbnailPresetId, setThumbnailPresetId] = useState<string | null>(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [renamePresetId, setRenamePresetId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [page, setPage] = useState(1);
  const [deleteConfirmPresetId, setDeleteConfirmPresetId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createTagId, setCreateTagId] = useState<string | null>(null);
  const [createThumbnailFile, setCreateThumbnailFile] = useState<File | null>(null);
  const [createThumbnailPreview, setCreateThumbnailPreview] = useState<string | null>(null);
  const createThumbnailInputRef = useRef<HTMLInputElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 6;

  const deletePreset = useDeleteWorkflowPreset();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setFilterDropdownOpen(false);
      }
    };
    if (filterDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterDropdownOpen]);

  const filteredPresets = useMemo(() => {
    const all = presets ?? [];
    const term = searchInput.trim().toLowerCase();
    return all
      .filter((p) => (showOnlyActive ? p.isActive : true))
      .filter((p) => {
        if (!term) return true;
        const haystack =
          `${p.name ?? ''} ${p.description ?? ''} ${(p.tags ?? []).join(' ')}`.toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => {
        const aFav = favoriteIds.includes(a.id);
        const bFav = favoriteIds.includes(b.id);
        if (aFav && !bFav) return -1;
        if (!aFav && bFav) return 1;
        return (a.name ?? '').localeCompare(b.name ?? '');
      });
  }, [presets, searchInput, showOnlyActive, favoriteIds]);

  const paginatedPresets = useMemo(
    () => filteredPresets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredPresets, page],
  );
  const totalPages = Math.ceil(filteredPresets.length / PAGE_SIZE) || 1;

  useEffect(() => {
    setPage(1);
  }, [searchInput, showOnlyActive]);

  const activeCount = useMemo(
    () => (presets ?? []).filter((p) => p.isActive).length,
    [presets],
  );

  const openCreateModal = () => {
    if (!requirePermission('workflow_presets.update', 'Criar Workflow Presets')) return;
    setCreateName('');
    setCreateTagId(null);
    setCreateThumbnailFile(null);
    if (createThumbnailPreview) {
      URL.revokeObjectURL(createThumbnailPreview);
    }
    setCreateThumbnailPreview(null);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateName('');
    setCreateTagId(null);
    setCreateThumbnailFile(null);
    if (createThumbnailPreview) {
      URL.revokeObjectURL(createThumbnailPreview);
    }
    setCreateThumbnailPreview(null);
  };

  const handleCreateThumbnailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (createThumbnailPreview) URL.revokeObjectURL(createThumbnailPreview);
    setCreateThumbnailFile(file);
    setCreateThumbnailPreview(URL.createObjectURL(file));
    e.target.value = '';
  }, [createThumbnailPreview]);

  const handleCreateFromModal = () => {
    if (!requirePermission('workflow_presets.update', 'Criar Workflow Presets')) return;
    const name = createName.trim();
    if (!name) return;
    const tagNames = createTagId
      ? [allTags.find((t) => t.id === createTagId)!.name]
      : [];
    const thumbnailFileToUpload = createThumbnailFile;
    createPreset.mutate(
      {
        name,
        description: '',
        calendarMode: 'CALENDAR_DAYS',
        graph: { nodes: [], edges: [] },
        tags: tagNames,
      },
      {
        onSuccess: async (created: WorkflowPresetDto) => {
          // Garante que usamos o id do preset que realmente acabou de ser criado com este nome:
          // se a API retornar outro preset (cache/race), buscamos na lista o que tem o nome enviado.
          let effectiveId = created?.id;
          let basePreset: WorkflowPresetDto = created;
          const nameMatches = created?.name?.trim() === name;
          if (!nameMatches || !effectiveId) {
            await queryClient.invalidateQueries({ queryKey: ['workflow-presets'], exact: true });
            await new Promise((r) => setTimeout(r, 100));
            const list = (await queryClient.fetchQuery({ queryKey: ['workflow-presets'] })) as WorkflowPresetDto[];
            const byName = (list ?? []).filter((p) => (p.name ?? '').trim() === name);
            const mostRecent = byName.sort(
              (a, b) =>
                new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
            )[0];
            if (mostRecent?.id) {
              effectiveId = mostRecent.id;
              basePreset = mostRecent;
            }
          }
          if (!effectiveId) return;
          let finalPreset: WorkflowPresetDto = { ...basePreset, id: effectiveId, name: basePreset.name ?? name };
          if (thumbnailFileToUpload) {
            try {
              const { url } = await uploadWorkflowPresetThumbnail(effectiveId, thumbnailFileToUpload);
              finalPreset = { ...finalPreset, thumbnailUrl: url };
            } catch {
              // segue sem miniatura
            }
          }
          queryClient.setQueryData(['workflow-presets', effectiveId], finalPreset);
          closeCreateModal();
          navigate({
            to: '/w/$workspaceId/workflow-presets/$presetId',
            params: { workspaceId, presetId: effectiveId },
          });
        },
      },
    );
  };

  const handleDeactivate = (e: React.MouseEvent, preset: WorkflowPresetDto) => {
    e.stopPropagation();
    setMenuOpenId(null);
    if (!requirePermission('workflow_presets.update', 'Editar workflow')) return;
    updatePreset.mutate({
      id: preset.id,
      payload: { isActive: false },
    });
  };

  const openRename = (preset: WorkflowPresetDto) => {
    setMenuOpenId(null);
    setRenamePresetId(preset.id);
    setRenameValue(preset.name ?? '');
  };

  const handleDeleteClick = (preset: WorkflowPresetDto) => {
    setMenuOpenId(null);
    setDeleteConfirmPresetId(preset.id);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmPresetId || !requirePermission('workflow_presets.update', 'Excluir workflow')) return;
    deletePreset.mutate(deleteConfirmPresetId, {
      onSuccess: () => {
        setDeleteConfirmPresetId(null);
        setPage((p) => Math.max(1, p - 1));
      },
    });
  };

  const submitRename = () => {
    if (!renamePresetId || !renameValue.trim() || !requirePermission('workflow_presets.update', 'Editar workflow')) return;
    updatePreset.mutate({
      id: renamePresetId,
      payload: { name: renameValue.trim() },
    });
    setRenamePresetId(null);
    setRenameValue('');
  };

  const tagsModalPreset = tagsModalPresetId
    ? (presets ?? []).find((p) => p.id === tagsModalPresetId)
    : null;

  const triggerThumbnailUpload = useCallback((presetId: string) => {
    setMenuOpenId(null);
    setThumbnailPresetId(presetId);
    setTimeout(() => thumbnailInputRef.current?.click(), 0);
  }, []);

  const handleThumbnailFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const presetId = thumbnailPresetId;
      if (!file || !presetId) return;
      setThumbnailUploading(true);
      setThumbnailPresetId(null);
      try {
        const { url } = await uploadWorkflowPresetThumbnail(presetId, file);
        queryClient.setQueryData(['workflow-presets', presetId], (prev: WorkflowPresetDto | undefined) =>
          prev ? { ...prev, thumbnailUrl: url } : prev,
        );
        queryClient.invalidateQueries({ queryKey: ['workflow-presets'], exact: true });
      } finally {
        setThumbnailUploading(false);
        e.target.value = '';
      }
    },
    [thumbnailPresetId, queryClient],
  );

  if (!canAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
        <Breadcrumb
          items={[
            { label: 'Meu Workspace', to: `/w/${workspaceId}/clients` },
            { label: 'Workflow Presets' },
          ]}
        />

        {/* Container principal - corner radius 10, sombra blur 15 */}
        <div
          className="bg-white p-6"
          style={{
            borderRadius: 10,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Título: Poppins regular 10px #06141B */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <div>
              <h1
                className="mb-1"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#06141B',
                }}
              >
                Workflows Presets
              </h1>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#4A5C6A' }}>
                {activeCount.toString().padStart(2, '0')} workflow
                {activeCount === 1 ? '' : 's'} ativos
              </p>
            </div>

            {/* Barra de pesquisa + Filtro + Criar workflow - mesmo padrão da lista de clientes */}
            <div className="flex items-center gap-3">
              <div className="relative w-[250px]">
                <input
                  type="text"
                  placeholder="Busca por workflow ou tag"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-0 placeholder-[#CCD0CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#06141B',
                    backgroundColor: '#F9FAFB',
                    borderRadius: 10,
                    outline: 'none',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none"
                  fill="none"
                  stroke="#CCD0CF"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <div className="relative" ref={filterDropdownRef}>
                <button
                  type="button"
                  onClick={() => setFilterDropdownOpen((v) => !v)}
                  className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80"
                  style={{
                    backgroundColor: '#06141B',
                    borderRadius: 10,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  title="Filtros"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>
                {filterDropdownOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 z-50 min-w-[200px] py-2 rounded-[10px] border border-gray-200 bg-white"
                    style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
                  >
                    <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showOnlyActive}
                        onChange={(e) => setShowOnlyActive(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs" style={{ color: '#06141B' }}>Apenas ativos</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => { setShowOnlyActive(false); setFilterDropdownOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded-b-[10px]"
                      style={{ color: '#06141B' }}
                    >
                      Limpar filtros
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={openCreateModal}
                className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80 disabled:opacity-60"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  backgroundColor: '#06141B',
                  color: '#F5F6F5',
                  fontWeight: 400,
                  borderRadius: 10,
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                }}
                title="Criar workflow"
              >
                +
              </button>
            </div>
          </div>

          {/* Cabeçalho das colunas */}
          <div
            className="grid items-center gap-6 px-5 py-3 border-b border-gray-100"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '11px',
              fontWeight: 500,
              color: '#9BA8AB',
              gridTemplateColumns: '32px 35px minmax(140px, 1fr) 100px 90px 48px minmax(90px, 130px) 40px',
            }}
          >
            <span className="flex justify-center" />
            <span className="flex justify-center" />
            <span>Workflow</span>
            <span className="flex justify-center">Criado em</span>
            <span className="flex justify-center">Status</span>
            <span className="flex justify-center">Criador</span>
            <span className="flex justify-center">Tag</span>
            <span className="flex justify-center" />
          </div>

          {/* Lista de cards */}
          <div className="flex flex-col gap-3" style={{ minHeight: 200 }}>
            {isLoading && (
              <div className="py-8 text-center text-[11px] text-gray-500">
                Carregando workflows...
              </div>
            )}

            {!isLoading && filteredPresets.length === 0 && (
              <div className="py-8 text-center text-[11px] text-gray-500">
                Nenhum Workflow Preset encontrado.
              </div>
            )}

            {!isLoading &&
              paginatedPresets.map((preset) => {
                const creator =
                  memberByUserId[preset.createdByUserId]?.name ?? 'Usuário desconhecido';
                const updatedAt = preset.updatedAt
                  ? new Date(preset.updatedAt)
                  : null;
                const updatedAtLabel = updatedAt
                  ? updatedAt.toLocaleDateString('pt-BR')
                  : '-';

                const createdAt = preset.createdAt
                  ? new Date(preset.createdAt).toLocaleDateString('pt-BR')
                  : '-';
                const creatorMember = memberByUserId[preset.createdByUserId];

                return (
                  <WorkflowPresetCard
                    key={preset.id}
                    preset={preset}
                    creatorName={creator}
                    creatorAvatarUrl={creatorMember?.avatarUrl ?? null}
                    updatedAtLabel={updatedAtLabel}
                    createdAtLabel={createdAt}
                    allTags={allTags}
                    isFavorite={favoriteIds.includes(preset.id)}
                    onToggleFavorite={(e) => toggleFavorite(e, preset.id)}
                    onNavigate={() =>
                      navigate({
                        to: '/w/$workspaceId/workflow-presets/$presetId',
                        params: { workspaceId, presetId: preset.id },
                      })
                    }
                    onOpenTagsModal={() => {
                      setMenuOpenId(null);
                      setTagsModalPresetId(preset.id);
                    }}
                    onTriggerThumbnailUpload={() => triggerThumbnailUpload(preset.id)}
                    onDeactivate={(e) => handleDeactivate(e, preset)}
                    onRename={() => {
                      setMenuOpenId(null);
                      openRename(preset);
                    }}
                    onDelete={() => handleDeleteClick(preset)}
                    requirePermission={requirePermission}
                    menuOpen={menuOpenId === preset.id}
                    onMenuToggle={() => setMenuOpenId((id) => (id === preset.id ? null : preset.id))}
                  />
                );
              })}
          </div>

          {/* Paginação - mesmo padrão da lista de clientes */}
          {!isLoading && totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                }}
              >
                &lt;
              </button>
              <span
                className="px-4 py-2"
                style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px' }}
              >
                Página {page} de {totalPages} ({filteredPresets.length}{' '}
                {filteredPresets.length === 1 ? 'workflow' : 'workflows'})
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                }}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de criação de workflow - portal acima da sidebar e header */}
      {createModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/30"
            style={{ zIndex: MODAL_OVERLAY_Z }}
            onClick={closeCreateModal}
          >
            <div
              className="bg-white rounded-[10px] shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-base font-medium text-gray-900">Novo Workflow Preset</h3>
              <p className="text-xs text-gray-500 mt-1">
                Defina o nome, a tag e a miniatura do workflow. Ao salvar, você será levado ao editor.
              </p>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Ex: Audiência de conciliação"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-1 focus:ring-[#06141B] focus:border-[#06141B]"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Tag</label>
                {allTags.length === 0 ? (
                  <p className="text-xs text-gray-500">Nenhuma tag cadastrada no workspace.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => {
                      const isSelected = createTagId === tag.id;
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => setCreateTagId((prev) => (prev === tag.id ? null : tag.id))}
                          className="inline-flex items-center px-3 py-1.5 rounded-full transition-colors"
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            border: 'none',
                            ...(isSelected
                              ? { backgroundColor: `${tag.color}18`, color: tag.color }
                              : { backgroundColor: '#F0F0F0', color: '#616A74' }),
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Miniatura</label>
                <input
                  ref={createThumbnailInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCreateThumbnailChange}
                />
                <div className="flex items-center gap-3">
                  <div
                    className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50"
                    style={{ minWidth: 64, minHeight: 64 }}
                  >
                    {createThumbnailPreview ? (
                      <img
                        src={createThumbnailPreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">Sem imagem</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => createThumbnailInputRef.current?.click()}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 text-gray-700"
                    >
                      {createThumbnailFile ? 'Trocar miniatura' : 'Escolher imagem'}
                    </button>
                    {createThumbnailFile && (
                      <button
                        type="button"
                        onClick={() => {
                          setCreateThumbnailFile(null);
                          if (createThumbnailPreview) URL.revokeObjectURL(createThumbnailPreview);
                          setCreateThumbnailPreview(null);
                        }}
                        className="text-xs text-red-600 hover:underline text-left"
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-2 justify-end">
              <button
                type="button"
                onClick={closeCreateModal}
                className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateFromModal}
                disabled={!createName.trim() || createPreset.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#06141B' }}
              >
                {createPreset.isPending ? 'Salvando...' : 'Salvar e abrir editor'}
              </button>
            </div>
          </div>
        </div>,
          document.body,
        )}

      {/* Modal de confirmação de exclusão - portal acima da sidebar e header */}
      {deleteConfirmPresetId &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/30"
            style={{ zIndex: MODAL_OVERLAY_Z }}
            onClick={() => setDeleteConfirmPresetId(null)}
          >
            <div
              className="bg-white rounded-[10px] p-6 shadow-xl max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <p
                className="mb-4"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#06141B',
                }}
              >
                Excluir este workflow preset? Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmPresetId(null)}
                  className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                  style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deletePreset.isPending}
                  className="px-4 py-2 rounded-lg text-white disabled:opacity-60"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#DC2626',
                  }}
                >
                  {deletePreset.isPending ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Modal de tags do workspace - portal acima da sidebar e header */}
      {tagsModalPreset &&
        createPortal(
          <WorkflowPresetTagsModal
            preset={tagsModalPreset}
            allTags={allTags}
            onClose={() => setTagsModalPresetId(null)}
            onSave={(tagNames) => {
              if (!requirePermission('workflow_presets.update', 'Editar tags do workflow')) return;
              updatePreset.mutate(
                { id: tagsModalPreset.id, payload: { tags: tagNames } },
                { onSuccess: () => setTagsModalPresetId(null) },
              );
            }}
            isSaving={updatePreset.isPending}
            onRename={() => {
              setTagsModalPresetId(null);
              openRename(tagsModalPreset);
            }}
            onDeactivate={(e) => {
              setTagsModalPresetId(null);
              handleDeactivate(e, tagsModalPreset);
            }}
            requirePermission={requirePermission}
            overlayStyle={{ zIndex: MODAL_OVERLAY_Z }}
          />,
          document.body,
        )}

      {/* Input oculto para upload de miniatura */}
      <input
        ref={thumbnailInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleThumbnailFileChange}
      />

      {/* Modal renomear - portal acima da sidebar e header */}
      {renamePresetId &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/30"
            style={{ zIndex: MODAL_OVERLAY_Z }}
            onClick={() => setRenamePresetId(null)}
          >
            <div
              className="bg-white rounded-[10px] p-6 shadow-xl max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-medium text-gray-900 mb-3">Alterar nome do workflow</h3>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Nome do workflow"
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => { setRenamePresetId(null); setRenameValue(''); }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submitRename}
                  disabled={!renameValue.trim() || updatePreset.isPending}
                  className="px-3 py-1.5 text-sm bg-[#06141B] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {updatePreset.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function WorkflowPresetTagsModal({
  preset,
  allTags,
  onClose,
  onSave,
  isSaving,
  onRename,
  onDeactivate,
  requirePermission,
  overlayStyle,
}: {
  preset: WorkflowPresetDto;
  allTags: TagDto[];
  onClose: () => void;
  onSave: (tagNames: string[]) => void;
  isSaving: boolean;
  onRename: () => void;
  onDeactivate: (e: React.MouseEvent) => void;
  requirePermission: (perm: string, action: string) => boolean;
  overlayStyle?: React.CSSProperties;
}) {
  const [selectedTagId, setSelectedTagId] = useState<string | null>(() => {
    const names = preset.tags ?? [];
    const first = allTags.find((t) => names.includes(t.name));
    return first?.id ?? null;
  });

  useEffect(() => {
    const names = preset.tags ?? [];
    const first = allTags.find((t) => names.includes(t.name));
    setSelectedTagId(first?.id ?? null);
  }, [preset.id, preset.tags, allTags]);

  const selectTag = (tagId: string) => {
    setSelectedTagId((prev) => (prev === tagId ? null : tagId));
  };

  const handleSave = () => {
    const tagNames = selectedTagId
      ? [allTags.find((t) => t.id === selectedTagId)!.name]
      : [];
    onSave(tagNames);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/30"
      style={overlayStyle}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[10px] shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-base font-medium text-gray-900">Tag do workspace</h3>
          <p className="text-xs text-gray-500 mt-1">
            Selecione uma tag para aplicar ao workflow &quot;{preset.name}&quot;
          </p>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {allTags.length === 0 ? (
            <p className="text-xs text-gray-500">Nenhuma tag cadastrada no workspace.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allTags.map((tag) => {
                const isSelected = selectedTagId === tag.id;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => selectTag(tag.id)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: 12,
                      fontWeight: 400,
                      border: 'none',
                      ...(isSelected
                        ? {
                            backgroundColor: `${tag.color}18`,
                            color: tag.color,
                          }
                        : {
                            backgroundColor: '#F0F0F0',
                            color: '#616A74',
                          }),
                    }}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-100 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full py-2.5 rounded-[10px] text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#06141B' }}
          >
            {isSaving ? 'Salvando...' : 'Salvar tag'}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onRename}
              className="flex-1 py-2 rounded-[10px] text-sm text-gray-700 border border-gray-200 hover:bg-gray-50"
            >
              Alterar nome
            </button>
            {preset.isActive && (
              <button
                type="button"
                onClick={(e) => {
                  if (requirePermission('workflow_presets.update', 'Desativar workflow')) onDeactivate(e);
                }}
                className="flex-1 py-2 rounded-[10px] text-sm text-red-600 border border-red-200 hover:bg-red-50"
              >
                Desativar
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatorAvatarWithTooltip({
  creatorName,
  creatorAvatarUrl,
}: {
  creatorName: string;
  creatorAvatarUrl: string | null;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showTooltip || !triggerRef.current) {
      setTooltipPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [showTooltip]);

  const tooltipContent = (
    <div
      className="fixed z-[100001] px-2 py-1 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none"
      style={{
        top: tooltipPosition?.top ?? 0,
        left: tooltipPosition?.left ?? 0,
        transform: 'translate(-50%, -100%)',
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#06141B',
      }}
    >
      {creatorName}
      <div
        className="absolute left-1/2 -bottom-1 w-2 h-2 transform -translate-x-1/2 rotate-45"
        style={{ backgroundColor: '#06141B' }}
      />
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className="relative flex justify-center flex-shrink-0"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {creatorAvatarUrl ? (
        <img
          src={creatorAvatarUrl}
          alt={creatorName}
          className="rounded-full object-cover flex-shrink-0 border-2 transition-colors cursor-default"
          style={{
            width: 36,
            height: 36,
            borderColor: '#F5F6F5',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#2D3748';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#F5F6F5';
          }}
        />
      ) : (
        <div
          className="rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors cursor-default"
          style={{
            width: 36,
            height: 36,
            backgroundColor: '#06141B',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '14px',
            fontWeight: 400,
            color: '#F5F6F5',
            borderColor: '#F5F6F5',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#2D3748';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#F5F6F5';
          }}
        >
          {(creatorName || '?').charAt(0).toUpperCase()}
        </div>
      )}
      {showTooltip && tooltipPosition && createPortal(tooltipContent, document.body)}
    </div>
  );
}

function WorkflowPresetCard({
  preset,
  creatorName,
  creatorAvatarUrl,
  updatedAtLabel,
  createdAtLabel,
  allTags,
  isFavorite,
  onToggleFavorite,
  onNavigate,
  onOpenTagsModal,
  onTriggerThumbnailUpload,
  onDeactivate,
  onRename,
  onDelete,
  requirePermission,
  menuOpen,
  onMenuToggle,
}: {
  preset: WorkflowPresetDto;
  creatorName: string;
  creatorAvatarUrl: string | null;
  updatedAtLabel: string;
  createdAtLabel: string;
  allTags: TagDto[];
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  onNavigate: () => void;
  onOpenTagsModal: () => void;
  onTriggerThumbnailUpload: () => void;
  onDeactivate: (e: React.MouseEvent) => void;
  onRename: () => void;
  onDelete: () => void;
  requirePermission: (perm: string, action: string) => boolean;
  menuOpen: boolean;
  onMenuToggle: () => void;
}) {
  const singleTag = useMemo(() => {
    const names = preset.tags ?? [];
    const first = allTags.find((t) => names.includes(t.name));
    return first ?? null;
  }, [preset.tags, allTags]);

  const gridCols = '32px 35px minmax(140px, 1fr) 100px 90px 48px minmax(90px, 130px) 40px';

  return (
    <div
      onClick={onNavigate}
      className="cursor-pointer transition-colors w-full py-3 px-5 grid items-center gap-6"
      style={{
        backgroundColor: '#F5F6F5',
        borderRadius: 10,
        boxShadow: '0 0 15px rgba(0, 0, 0, 0.05)',
        gridTemplateColumns: gridCols,
      }}
    >
      {/* Favorito */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onToggleFavorite}
          className="p-0.5 hover:opacity-80 transition-opacity"
          title={isFavorite ? 'Desfavoritar' : 'Favoritar'}
        >
          <FavoriteStarIcon filled={isFavorite} size={19} />
        </button>
      </div>

      {/* Miniatura workflow 35x35 */}
      <div className="flex justify-center">
        <div
          className="overflow-hidden flex items-center justify-center bg-gray-200 flex-shrink-0"
          style={{ width: 35, height: 35, borderRadius: 5 }}
        >
          {preset.thumbnailUrl ? (
            <img src={preset.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[8px] text-gray-400">—</span>
          )}
        </div>
      </div>

      {/* Nome + Última edição (sem criado em) */}
      <div className="min-w-0">
        <p
          className="truncate"
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: '12px',
            fontWeight: 400,
            color: '#06141B',
          }}
        >
          {preset.name}
        </p>
        <p
          className="truncate mt-0.5"
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: '10px',
            fontWeight: 400,
            color: '#9BA8AB',
          }}
        >
          Última edição {updatedAtLabel} por {creatorName}
        </p>
      </div>

      {/* Coluna Criado em */}
      <div className="flex justify-center">
        <p
          className="truncate text-[10px] max-w-full"
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 400,
            color: '#9BA8AB',
          }}
        >
          {createdAtLabel}
        </p>
      </div>

      {/* Coluna Status (ativo/inativo) - centralizado */}
      <div className="flex justify-center">
        <span
          className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-normal"
          style={{
            fontFamily: "'Poppins', sans-serif",
            backgroundColor: preset.isActive ? '#E8F5E9' : '#F0F0F0',
            color: preset.isActive ? '#2E7D32' : '#616A74',
            border: 'none',
          }}
        >
          {preset.isActive ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      {/* Coluna Criador - miniatura/foto de perfil com tooltip igual à lista de clientes */}
      <CreatorAvatarWithTooltip
        creatorName={creatorName}
        creatorAvatarUrl={creatorAvatarUrl}
      />

      {/* Coluna Tag única - centralizada */}
      <div className="flex justify-center items-center min-w-0">
        {singleTag ? (
          <span
            className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-normal truncate max-w-full"
            style={{
              fontFamily: "'Poppins', sans-serif",
              backgroundColor: `${singleTag.color}18`,
              color: singleTag.color,
              border: 'none',
            }}
          >
            {singleTag.name}
          </span>
        ) : (preset.tags ?? [])[0] ? (
          <span
            className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-normal truncate max-w-full"
            style={{
              fontFamily: "'Poppins', sans-serif",
              backgroundColor: '#F0F0F0',
              color: '#616A74',
              border: 'none',
            }}
          >
            {(preset.tags ?? [])[0]}
          </span>
        ) : (
          <span className="text-[10px] text-gray-400">—</span>
        )}
      </div>

      {/* Três pontinhos - menu com Tags, Miniatura, Nome, Desativar, Excluir */}
      <div className="relative flex justify-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onMenuToggle();
          }}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200/80 transition-colors text-gray-600"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="6" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="18" r="1.5" />
          </svg>
        </button>
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={(e) => { e.stopPropagation(); onMenuToggle(); }}
            />
            <div
              className="absolute right-0 top-full mt-1 z-20 py-1 min-w-[180px] rounded-lg border border-gray-200 bg-white shadow-lg"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenTagsModal(); }}
                className="w-full text-left px-3 py-2 text-[12px] text-gray-700 hover:bg-gray-50 rounded-t-lg"
              >
                Gerenciar tags
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onTriggerThumbnailUpload(); }}
                className="w-full text-left px-3 py-2 text-[12px] text-gray-700 hover:bg-gray-50"
              >
                Alterar miniatura
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRename(); }}
                className="w-full text-left px-3 py-2 text-[12px] text-gray-700 hover:bg-gray-50"
              >
                Alterar nome
              </button>
              {preset.isActive && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (requirePermission('workflow_presets.update', 'Desativar workflow')) {
                      onDeactivate(e);
                    }
                  }}
                  className="w-full text-left px-3 py-2 text-[12px] text-red-600 hover:bg-red-50"
                >
                  Desativar
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (requirePermission('workflow_presets.update', 'Excluir workflow')) {
                    onDelete();
                  }
                }}
                className="w-full text-left px-3 py-2 text-[12px] text-red-600 hover:bg-red-50 rounded-b-lg"
              >
                Excluir
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
